'use client';

import { useState, useCallback, useEffect, useRef } from 'react';

export interface UsePaneResizeOptions {
    minWidth: number;
    maxWidth: number;
    defaultWidth: number;
}

export interface UsePaneResizeReturn {
    width: number;
    isDragging: boolean;
    handleMouseDown: (e: React.MouseEvent) => void;
}

/**
 * Hook for managing resizable pane width via mouse dragging.
 * Adds/removes global event listeners and cursor styles during drag.
 */
export function usePaneResize(options: UsePaneResizeOptions): UsePaneResizeReturn {
    const { minWidth, maxWidth, defaultWidth } = options;
    const [width, setWidth] = useState(defaultWidth);
    const [isDragging, setIsDragging] = useState(false);
    const containerLeftRef = useRef(0);

    const handleMouseDown = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        // Capture the container's left offset relative to viewport
        const container = (e.currentTarget as HTMLElement).parentElement;
        if (container) {
            containerLeftRef.current = container.getBoundingClientRect().left;
        }
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent): void => {
            if (!isDragging) return;
            // Calculate width relative to container's left edge, not viewport
            const relativeX = e.clientX - containerLeftRef.current;
            const newWidth = Math.min(maxWidth, Math.max(minWidth, relativeX));
            setWidth(newWidth);
        },
        [isDragging, minWidth, maxWidth],
    );

    const handleMouseUp = useCallback((): void => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return (): void => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    return { width, isDragging, handleMouseDown };
}
